import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Paper,
  Divider,
  Typography,
} from '@mui/material';
import { radius } from '../../../../../theme';
import ResumoNegocios from './resumoNegocios';
import CustosBovespa from './custosBovespa';
import CustosCorretagem from './custosCorretagem';
import CabecalhoNota from '../cabecalhoNota';
import { filter, forEach, isEmpty, map, sumBy } from 'lodash';
import { useUsuario } from '../../../../../providers/usuarioProvider';
import { useSnackBar } from '../../../../../stores/snackBarStores';
import FelixAnalytics from '../../../../../felixAnalytics';
import {
  IconArrowDownCircle,
  IconArrowLeft,
  IconFileUpload,
  IconTrashX,
} from '@tabler/icons-react';
import { NotaBovespa } from '../../../../../models/nota';
import { OperacaoLeitorBovespa } from '../../../../../models/operacao';
import {
  Negociacao,
  SituacaoNota,
  TipoOperacao,
} from '../../../../../models/enum';
import Operacoes from '../operacoes';
import {
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import NotaDb from '../../../../../db/notaDb';
import OperacaoDb from '../../../../../db/operacaoDb';
import Functions from '../../../../../callable/functions';
import { useNavigate } from 'react-router-dom';

type Props = { nota: NotaBovespa; nova: boolean };

export default function Bovespa({ nota: notaCadastrada, nova }: Props) {
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const snackBar = useSnackBar();
  const [nota, setNota] = useState<Partial<NotaBovespa>>(
    notaCadastrada || {
      corretora: '',
      dataPregao: undefined,
      numeroNota: undefined,
      taxaLiquidacao: 0.0,
      taxaRegistro: 0.0,
      emolumentos: 0.0,
      corretagem: 0.0,
      iss: 0.0,
      irrf: 0.0,
      outrosCustos: 0.0,
      tipoNegociacao: Negociacao.BOVESPA,
    },
  );

  const [operacoes, setOperacoes] = useState<OperacaoLeitorBovespa[]>([]);

  useEffect(() => {
    if (!!visualizado && !!notaCadastrada) {
      onSnapshot(
        query(
          new OperacaoDb().collection({
            usuarioId: visualizado,
            operacaoId: '',
          }),
          where('nota', '==', notaCadastrada.id),
        ),
        (operacoesSnapshot) => {
          setOperacoes(
            map(
              operacoesSnapshot.docs,
              (doc) => doc.data() as OperacaoLeitorBovespa,
            ),
          );
        },
      );
    }
  }, [notaCadastrada]);

  const [erros, setErros] = useState({});
  const setPropriedadeNota = (nome: string, valor: any) => {
    setNota({ ...nota, [nome]: valor });
  };

  const salvarNota = async () => {
    const camposComErro = camposObrigatoriosVazios(nota);
    if (isEmpty(camposComErro) && !!visualizado) {
      const notaASalvar = new NotaBovespa({
        id: '',
        usuario: visualizado,
        dataPregao: nota.dataPregao!.startOf('day'),
        situacao: SituacaoNota.Nova,
        corretora: nota.corretora!,
        numeroNota: nota.numeroNota!,
        taxaLiquidacao: nota.taxaLiquidacao!,
        emolumentos: nota.emolumentos!,
        corretagem: nota.corretagem!,
        tipoNegociacao: Negociacao.BOVESPA,
        quantidadeOperacoes: operacoes.length!,
        valorLiquido: sumBy(operacoes, (operacao: OperacaoLeitorBovespa) =>
          operacao.tipoOperacao === TipoOperacao.VENDA
            ? operacao.valorTotal
            : -operacao.valorTotal,
        ),
        taxaRegistro: nota.taxaRegistro!,
        iss: nota.iss!,
        irrf: nota.irrf!,
        outrosCustos: nota.outrosCustos!,
      });
      const batch = writeBatch(getFirestore());
      const docRef = doc(
        new NotaDb().collection({ usuarioId: visualizado, notaId: '' }),
      );
      batch.set(docRef, notaASalvar);
      forEach(operacoes, (operacao, indexOperacao) => {
        operacao.ordem = indexOperacao + 1;
        operacao.nota = docRef.id;
        operacao.usuario = visualizado;
        operacao.dataPregao = notaASalvar.dataPregao;
        batch.set(
          doc(
            new OperacaoDb().collection({
              usuarioId: visualizado,
              operacaoId: '',
            }),
          ),
          operacao,
        );
      });
      try {
        await batch.commit();
        new Functions().prepararNotaManual({
          notaId: docRef.id,
        });
        navigate(-1);
        snackBar({
          type: 'ALERT',
          payload: {
            mensagem: 'Pronto, já guardei sua nota aqui!',
            severidade: 'success',
          },
        });
        FelixAnalytics.identify(visualizado);
        FelixAnalytics.track('nota_cadastrada', { tipo: 'manual' });
      } catch (error) {
        snackBar({
          type: 'ALERT',
          payload: {
            mensagem: 'Ops, algo deu errado!',
            severidade: 'error',
          },
        });
      }
    }
    setErros(camposComErro);
  };

  const handleClose = () => {
    setAbrirPopupExclusao(false);
  };
  const handleOpen = () => {
    setAbrirPopupExclusao(true);
  };
  const [abrirPopupExclusao, setAbrirPopupExclusao] = useState(false);
  const apagarNota = () => {
    if (!!visualizado && !!nota.id) {
      deleteDoc(
        new NotaDb().doc({ usuarioId: visualizado, notaId: nota.id }),
      ).then(() => {
        snackBar({
          type: 'ALERT',
          payload: {
            mensagem: 'Já apaguei aquela nota, viu?',
            severidade: 'success',
          },
        });
        FelixAnalytics.identify(visualizado);
        FelixAnalytics.track('nota-apagada', { nota: notaCadastrada.id });
      });
      navigate(-1);
    }
  };

  const camposObrigatoriosVazios = (nota: Partial<NotaBovespa>) => {
    let camposComErro = {};
    if (!nota.corretora) camposComErro = { ...camposComErro, corretora: true };
    if (!nota.dataPregao)
      camposComErro = {
        ...camposComErro,
        dataPregao: true,
      };
    if (!nota.numeroNota)
      camposComErro = {
        ...camposComErro,
        numeroNota: true,
      };
    if (operacoes.length === 0)
      camposComErro = { ...camposComErro, operacoes: true };

    return camposComErro;
  };

  return (
    <Container maxWidth="lg">
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={4}
        pb={2}
      >
        <Box flexGrow={1} alignSelf="center">
          <Typography variant="eyebrow" color="text.secondary">
            Nota de corretagem — Bovespa
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          startIcon={<IconArrowLeft />}
        >
          Voltar
        </Button>
        <Box m={1 / 2} />
        {nova ? (
          <Button
            variant="contained"
            color="success"
            onClick={salvarNota}
            startIcon={<IconArrowDownCircle />}
          >
            Salvar nota
          </Button>
        ) : (
          <>
            {notaCadastrada?.upload && (
              <>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() =>
                    navigate(`/arquivo/upload/${notaCadastrada.upload}`)
                  }
                  startIcon={<IconFileUpload />}
                >
                  Ver upload
                </Button>
                <Box m={1 / 2} />
              </>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={handleOpen}
              startIcon={<IconTrashX />}
            >
              Apagar
            </Button>
            <Dialog
              open={abrirPopupExclusao}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                Quer mesmo apagar essa nota?
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Não será possível desfazer essa operação.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="inherit">
                  Cancelar
                </Button>
                <Button onClick={apagarNota} variant="contained" color="error">
                  Apagar
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
      <Box pb={4}>
        {/* Painel grande: radius xl + hairline, sem sombra (DESIGN.md → Cards). */}
        <Paper variant="outlined" sx={{ borderRadius: `${radius.xl}px` }}>
          <Box paddingX={1} paddingY={2}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12}>
                <CabecalhoNota
                  setPropriedadeNota={setPropriedadeNota}
                  corretora={nota.corretora}
                  dataPregao={nota.dataPregao}
                  numeroNota={nota.numeroNota}
                  erros={erros}
                  readOnly={!nova}
                />
              </Grid>
              <Grid item xs={12}>
                <Box paddingX={1} paddingY={1}>
                  <Operacoes
                    operacoes={operacoes}
                    dataPregao={nota.dataPregao}
                    usuario={visualizado}
                    setOperacoes={(ops) =>
                      setOperacoes(ops as OperacaoLeitorBovespa[])
                    }
                    readOnly={!nova}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider orientation="horizontal" variant="fullWidth" />
              </Grid>
              <Grid item xs={4}>
                <ResumoNegocios
                  compras={sumBy(
                    filter(
                      operacoes,
                      (operacao) =>
                        operacao.tipoOperacao === TipoOperacao.COMPRA,
                    ),
                    (operacao) => operacao.valorTotal,
                  )}
                  vendas={sumBy(
                    filter(
                      operacoes,
                      (operacao) =>
                        operacao.tipoOperacao === TipoOperacao.VENDA,
                    ),
                    (operacao) => operacao.valorTotal,
                  )}
                  valorLiquido={sumBy(
                    operacoes,
                    (operacao) => operacao.valorTotal,
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <CustosBovespa
                  setPropriedadeNota={setPropriedadeNota}
                  taxaLiquidacao={nota.taxaLiquidacao}
                  taxaRegistro={nota.taxaRegistro}
                  emolumentos={nota.emolumentos}
                  readOnly={!nova}
                />
              </Grid>
              <Grid item xs={4}>
                <CustosCorretagem
                  setPropriedadeNota={setPropriedadeNota}
                  corretagem={nota.corretagem}
                  iss={nota.iss}
                  irrf={nota.irrf}
                  outrosCustos={nota.outrosCustos}
                  totalCorretagem={
                    nota.corretagem! +
                    nota.iss! +
                    nota.irrf! +
                    nota.outrosCustos!
                  }
                  readOnly={!nova}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
